import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, Wind, Wrench } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const WELLSIDE_ACCESSORIES_HERO = "/products/bushtech-accessories/731077_2bd176a1581249e8bda53c6d6a28ce31~mv2.jpg";
const fmt = (n) => n.toLocaleString("en-NZ", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Already native elsewhere on the site — cross-linked here, not duplicated.
// Grouped with the native EEZI-AWN K9 Platform Rack so every roof-rack /
// load-bar accessory (EEZI-AWN and Quick Pitch alike) sits together in one
// cluster, rather than being scattered by source (native vs. cross-linked).
const WELLSIDE_ACCESSORY_ROOFRACK_PRODUCTS = [
  { name: "K9 Load Bars for Bushtech Canopy", price: 495, priceFrom: true, thumb: "/products/roofracks/731077_2dd9e5f302a94557839021bb5042a76a~mv2.jpg", to: "/roof-racks/k9-load-bars-bushtech-canopy" },
  { name: "Quick Pitch 1450mm Load Bars, Pair", price: 449, thumb: "/products/bushtech-accessories/731077_0c22768144d64606ab354a02c65f6edf~mv2.jpg", to: "/roof-racks/quick-pitch-1450mm-load-bars-pair" },
];

// Promoted into the main grid (canopy-specific accessories that belong here front and centre):
const WELLSIDE_ACCESSORY_PROMOTED_PRODUCTS = [
  { name: "National Luna LED Touch Light — White", price: 79, thumb: "/products/fridges/731077_ed7872f954cf40d0aee4f5c040687a3e~mv2.jpg", to: "/12v-fridges/national-luna-led-touch-light-white" },
  { name: "National Luna 18+12 LED Dual Colour Touch Light", price: 109, thumb: "/products/fridges/731077_83ea6cc345c749038b7b1650fb3b4a3d~mv2.jpg", to: "/12v-fridges/national-luna-18-12-led-dual-colour" },
  { name: "Bushtech Drawers w/ Powered Fridge & 55L Water Tank", price: 4690, thumb: "/products/drawers/731077_5d9e25809aea44d998cf28168b514f84~mv2.jpg", to: "/drawers-kitchens-tables/bushtech-drawers-powered-fridge-55l-water-tank" },
];

// Kept in the secondary "Already In Our Range" section below
const WELLSIDE_ACCESSORY_LINKED_PRODUCTS = [
  { name: "Quick Pitch Complete Mini Kitchen", price: 3499, thumb: "/products/drawers/731077_c751d09447894393a61cd25c8a2bfe7b~mv2.jpg", to: "/drawers-kitchens-tables/quick-pitch-complete-mini-kitchen" },
  { name: "Quick Pitch Kitchen Drawer", price: 2099, priceFrom: true, thumb: "/products/drawers/731077_aa6eed1599194a4787a8594f363f3fad~mv2.jpg", to: "/drawers-kitchens-tables/quick-pitch-kitchen-drawer" },
  { name: "Quick Pitch Drawers", price: 995, priceFrom: true, thumb: "/products/drawers/731077_a53f2e6d59c04742911ea8d7bc6df957~mv2.jpg", to: "/drawers-kitchens-tables/quick-pitch-drawers" },
  { name: "Stainless Steel Table w/ Roof Bracket", price: 599, thumb: "/products/drawers/731077_7503a6ea219341d8bcca85c31943b595~mv2.png", to: "/drawers-kitchens-tables/stainless-steel-table-roof-bracket" },
];

const WHY = [
  { icon: Lock, t: "Security & Dust Sealing", d: "Central locking, security mesh and dust sealing kits — extra security, plus finer dust sealing for the harshest gravel roads." },
  { icon: Wind, t: "Genuine EEZI-AWN Engineering", d: "Platform racks and load bars built to the same expedition-grade standard as the canopy itself." },
  { icon: Wrench, t: "Purpose-Matched, Not Universal", d: "Every accessory here slots straight into a Bushtech canopy's standard tracks and subframe — no adapters." },
];

const priceLabel = (m) => `${m.priceFrom ? "FROM " : ""}$${fmt(m.price)}`;

const Card = ({ m, testid }) => (
  <Link
    to={m.to || `/wellside-accessories/${m.slug}`}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      {m.badge && (
        <div className="absolute top-4 left-4">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">{m.badge}</span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-lg md:text-xl text-white leading-tight tracking-wide">{m.name.toUpperCase()}</h3>
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-2 line-clamp-1">{m.tagline}</p>
        <div className="flex items-end justify-between gap-3 mt-4">
          <p className="font-display text-xl text-[var(--brand-primary)] leading-none">{priceLabel(m)}</p>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

export default function WellsideAccessoriesPage() {
  const [products, setProducts] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/wellsideaccessory`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="wellside-accessories-page">
      <SEO
        path="/wellside-accessories"
        title="Wellside Canopy Accessories NZ · Bushtech & EEZI-AWN"
        description="Central locking, dust sealing, security mesh, K9 platform racks, storage systems and more — genuine accessories purpose-matched to every Bushtech Wellside canopy."
        image={WELLSIDE_ACCESSORIES_HERO}
      />

      <section className="relative min-h-[80svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={WELLSIDE_ACCESSORIES_HERO} alt="Bushtech canopy accessories" className="w-full h-full object-cover" style={{ objectPosition: "50% 45%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— Wellside Canopy Accessories</p>
          <h1 className="font-display text-[11vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.88] text-white hero-text-shadow max-w-[20ch]">
            GET MORE FROM<br />
            <span className="text-[var(--brand-primary)]">YOUR CANOPY.</span>
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 leading-relaxed max-w-xl [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              Central locking, security mesh, dust sealing, platform racks and storage systems — every accessory here is purpose-built to fit a Bushtech aluminium canopy.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a href="#range" data-testid="wellside-accessories-cta-range" className="btn-primary">View Range <ArrowUpRight size={14} /></a>
              <a href="#enquire" className="btn-secondary">Enquire</a>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <div key={i} data-testid={`wellside-accessories-why-${i}`} className="bg-[var(--bg-main)] p-6 md:p-8">
                <Icon size={22} className="text-[var(--brand-primary)] mb-4" strokeWidth={1.6} />
                <h3 className="font-display text-lg md:text-xl text-white mb-2">{w.t}</h3>
                <p className="text-base text-[var(--text-secondary)] leading-relaxed">{w.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section id="range" className="container-x pb-16 md:pb-20 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— The Range</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              CANOPY <span className="text-[var(--brand-primary)]">ACCESSORIES.</span>
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((m, i) => (
            <>
              <Card key={m.slug} m={m} testid={`wellside-accessory-item-${i}`} />
              {m.slug === "eezi-awn-k9-platform-rack-bushtech-canopy" &&
                WELLSIDE_ACCESSORY_ROOFRACK_PRODUCTS.map((rp, j) => (
                  <Card key={rp.to} m={rp} testid={`wellside-accessory-roofrack-${j}`} />
                ))}
            </>
          ))}
          {WELLSIDE_ACCESSORY_PROMOTED_PRODUCTS.map((m, i) => <Card key={m.to} m={m} testid={`wellside-accessory-promoted-${i}`} />)}
        </div>
      </section>

      {/* ALREADY IN OUR RANGE — cross-linked, not duplicated */}
      <section className="container-x pb-16 md:pb-20 pt-16 md:pt-20 border-t border-[var(--border-default)]">
        <div className="mb-8">
          <p className="overline mb-3">— Already In Our Range</p>
          <h2 className="font-display text-3xl md:text-4xl text-white leading-[0.95]">
            MORE CANOPY-COMPATIBLE <span className="text-[var(--brand-primary)]">GEAR.</span>
          </h2>
          <p className="text-[var(--text-secondary)] mt-4 max-w-2xl leading-relaxed">
            These already live elsewhere in the range — linked here so you don't miss them.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WELLSIDE_ACCESSORY_LINKED_PRODUCTS.map((p, i) => (
            <Link
              key={p.to}
              to={p.to}
              data-testid={`wellside-accessories-linked-${i}`}
              className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors"
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={p.thumb} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-display text-base md:text-lg text-white leading-tight">{p.name}</h3>
                  <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-2">{p.priceFrom ? "FROM " : ""}${fmt(p.price)}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
